import { Injectable } from '@angular/core';
import { objectShallowEquality } from '@app/modules/location-client/utilities';
import { LeafletService } from '@app/modules/location/services/leaflet.service';
import { ReverseGeocoderService } from '@app/modules/reverse-geocoder/services/reverse-geocoder.service';
import { AppState } from '@app/store';
import {
  selectAllAssets,
  selectAllVehicles,
  selectAssetParams,
  selectAssetsLoadState,
  selectChosenAsset
} from '@app/store/asset/selectors/assets.selectors';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import {
  catchError,
  delay,
  distinctUntilChanged,
  expand,
  filter,
  first,
  map,
  mergeMap,
  reduce,
  skip,
  skipUntil,
  switchMap,
  takeUntil,
  takeWhile
} from 'rxjs/operators';
import { EMPTY, forkJoin, from, Observable, of, Subject } from 'rxjs';
import * as AssetsActions from '../actions/assets.actions';
import { startAssetsPollingOnInitialLoad } from '../actions/assets.actions';
import { DataDogService } from '@app/services/data-dog.service';
import { LiveAssetQueryService } from '@app/services/live-asset-query.service';
import { Asset, AssetsParams, DeviceType } from '@app/modules/location-client/location-api.models';
import { environment } from '@environments/environment';
import { RecentPathService } from '@app/modules/location/services/recent-path.service';
import * as LayoutActions from '@app/store/layout/actions/layout.actions';
import { DetailsSubcontext, ViewContext } from '@app/store/layout/reducers/layout.reducer';
import { HttpCancelService } from '@app/services/http-cancel.service';
import { AssetIoService } from '@app/modules/asset-io/asset-io.service';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { SelectedAssetService } from '@app/services/selected-asset.service';
import { selectViewSubContext } from '@app/store/layout/selectors/layout.selectors';
import { selectDefaultFilter } from '@app/store/filters/selectors/filters.selectors';
import { MarkerIconService } from '@app/modules/location/services/marker-icon.service';
import { getDistance, patchAssetData, patchAssetsData, round } from '@app/modules/shared/utilities/utilities';
import { ViewableAsset } from '@app/modules/location/models/viewable-asset.model';
import { UiUtilities } from '@app/services/ui-utilities';
import { VehiclesApiService } from '@app/services/vehicles-api.service';
import { selectAllSettings } from '@app/store/settings/selectors/settings.selectors';
import { DaysWithActivityResponse } from '@app/modules/location/models/days-with-activity.model';
import { PathDistanceUnits } from '@app/modules/location/models/path-api.model';
import { DriverStatusService } from '@app/modules/driver-availability/services/driver-status.service';
import { DriverActivity, DriverActivityResponse } from '@app/modules/driver-availability/models/driver-status.model';
import { DriverAssetService } from '@app/modules/driver/services/driver-asset.service';
import { ApiDriverAsset, DriverVehicleAssociation } from '@app/modules/driver/models/driver.models';
import { Trips } from '@app/modules/location/models/trip.model';
import { DateTime, Interval } from 'luxon';
import { VehicleList, VehicleListItem } from '@app/core/models/zonar-accounts.model';

@Injectable()
export class AssetsEffects {
  cancelSubject$ = new Subject<boolean>();
  assetsParams: AssetsParams;
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private datadog: DataDogService,
    private leaflet: LeafletService,
    private liveAssetsQuery: LiveAssetQueryService,
    private recentPathService: RecentPathService,
    private driverStatusService: DriverStatusService,
    private httpCancelService: HttpCancelService,
    private geocoder: ReverseGeocoderService,
    private assetIoService: AssetIoService,
    private selectedAssetService: SelectedAssetService,
    private driverAssetService: DriverAssetService,
    private vehiclesApi: VehiclesApiService,
    private markerIconService: MarkerIconService,
    private uiUtils: UiUtilities
  ) {}

  loadAssets$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssetsActions.loadAssets),
      distinctUntilChanged((prev, next) => objectShallowEquality(prev.assetsParams, next.assetsParams)),
      concatLatestFrom(() => [
        this.store.select(selectViewSubContext),
        this.store.select(selectAllVehicles),
        this.store.select(selectAllSettings)
      ]),
      filter(([action, viewSubContext, vehiclesMap, settings]) => {
        // ZTT-1200: viewSubContext needed so that other assets do not load in history, as there were indeterminate times assets would load on map in history view.
        // absent this check for viewSubContext, pins will appear on the map after reloading while in history view and selecting a company
        return (
          action?.assetsParams?.companyId !== null &&
          action.assetsParams?.companyId !== undefined &&
          viewSubContext !== DetailsSubcontext.HISTORY
        );
      }),
      mergeMap(([action, viewSubContext, vehiclesMap, settings]) => {
        this.resetSubscriptions();
        this.assetsParams = { ...action.assetsParams };
        return this.liveAssetsQuery.executeAssetQueryWithParams(action.assetsParams).pipe(
          map(assets => {
            return AssetsActions.loadAssetsSuccess({
              response: patchAssetsData(assets, vehiclesMap, settings.assetViewType)
            });
          }),
          catchError(assetsLoadError => {
            this.datadog.addRumError(assetsLoadError);
            return of(AssetsActions.loadAssetsFailure({ assetsLoadError }));
          })
        );
      })
    );
  });

  loadAssetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssetsActions.loadAssetsSuccess),
        map(action => {
          if (action.response?.length >= 0) {
            this.leaflet.zoomToAssets(action.response);
            this.leaflet.refreshMap(action.response);
            this.store.dispatch(startAssetsPollingOnInitialLoad());
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * This is the assets polling effect
   */
  startAssetsPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.startAssetsPolling),
      concatLatestFrom(() => this.store.select(selectAssetParams)),
      switchMap(([_, assetsParams]) => {
        this.assetsParams = { ...assetsParams };
        return of(AssetsActions.updateAssets({ assetsParams }));
      })
    )
  );

  updateAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.updateAssets),
      // This is the polling mechanism
      concatLatestFrom(() => [
        this.store.select(selectViewSubContext),
        this.store.select(selectAllVehicles),
        this.store.select(selectAllSettings)
      ]),
      switchMap(([action, viewSubContext, vehiclesMap, settings]) => {
        return this.liveAssetsQuery.executeAssetQueryWithParams(action.assetsParams).pipe(
          // additional insurance policy in the front of the pipeline to cut the event if we change params mid-stream.
          takeWhile(() => {
            return (
              viewSubContext === DetailsSubcontext.LIVE && objectShallowEquality(this.assetsParams, action.assetsParams)
            );
          }),
          switchMap(assets => {
            return of(
              AssetsActions.loadAssetsForIntervalSuccess({
                response: patchAssetsData(assets, vehiclesMap, settings.assetViewType),
                assetsParams: action.assetsParams
              })
            );
          }),
          takeUntil(this.httpCancelService.onCancelLocApiPendingRequests()),
          takeUntil(this.cancelSubject$)
        );
      })
    )
  );

  // used to force asset list update after vdo drivers have been loaded
  // updates driver names
  forceUpdateAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.forceUpdateAssets),
      concatLatestFrom(() => [this.store.select(selectAllAssets), this.store.select(selectAssetParams)]),
      switchMap(([action, assets, params]) => {
        return of(
          AssetsActions.loadAssetsForIntervalSuccess({
            response: [...assets],
            assetsParams: params
          })
        );
      })
    )
  );

  loadAssetsOnIntervalSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.loadAssetsForIntervalSuccess),
      switchMap(action => {
        this.leaflet.refreshMap(action.response);
        return of(action).pipe(
          delay(environment.liveUpdate.pollingInterval),
          switchMap(() => of(AssetsActions.updateAssets({ assetsParams: action.assetsParams })))
        );
      })
    )
  );

  /**
   * Delayed asset polling on initial load.
   */
  startAssetsPollingOnInitialLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.startAssetsPollingOnInitialLoad),
      delay(environment.liveUpdate.pollingInterval),
      map(() => {
        return AssetsActions.startAssetsPolling();
      })
    )
  );

  stopAssetsPolling$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssetsActions.stopAssetsPolling),
        map(() => {
          this.resetSubscriptions();
        })
      ),
    { dispatch: false }
  );

  selectLive$ = createEffect(() => {
    // when the application bootstraps, 1x of the setViewSubContext action is fired.
    // at that time, polling has not started, so there is no need to restart it
    // this `skipWhile` prevents 1x unnecessary api call during application bootstrap
    const assetsInitiallyLoaded$ = this.store
      .select(selectAssetsLoadState)
      .pipe(filter(assetsLoadState => assetsLoadState === ResourceLoadState.LOAD_SUCCESSFUL));

    return this.actions$.pipe(
      ofType(LayoutActions.setViewSubContext),
      skipUntil(assetsInitiallyLoaded$),
      filter(({ subContext }) => subContext === DetailsSubcontext.LIVE),
      map(() => {
        return AssetsActions.startAssetsPolling();
      })
    );
  });

  selectHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.setViewSubContext),
      concatLatestFrom(() => this.store.select(selectChosenAsset)),
      filter(
        ([action, selectedAsset]) => action.subContext === DetailsSubcontext.HISTORY && Boolean(selectedAsset?.assetId)
      ),
      mergeMap(([_, selectedAsset]) => {
        this.store.dispatch(AssetsActions.stopAssetsPolling());
        return of(AssetsActions.loadRecentPath({ selectedAsset }));
      })
    )
  );

  selectList$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LayoutActions.setViewContext),
        filter(({ context }) => context === ViewContext.LIST),
        skip(1),
        concatLatestFrom(() => this.store.select(selectAllAssets)),
        map(([action, assets]) => {
          this.leaflet.setSelectedAsset(null);
        })
      );
    },
    { dispatch: false }
  );

  selectAssetForLeaflet$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssetsActions.loadSelectedAsset),
        map(action => action?.selectedAsset),
        map(selectedAsset => {
          if (selectedAsset?.latitude && selectedAsset?.longitude) {
            // this ensures that if a user gets nearby assets, then clicks a new map marker from nearby assets, that new map marker will not have "X miles awy" on the pin
            const asset = {
              ...selectedAsset,
              markerSubtitle: ''
            };
            this.leaflet.zoomToAssets([asset]);
            this.leaflet.setSelectedAsset(asset);
          } else {
            this.leaflet.setSelectedAsset(null);
          }
          this.store.dispatch(AssetsActions.clearNearbyAssets());
        })
      ),
    { dispatch: false }
  );

  loadAssetGeocode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.loadSelectedAsset),
      filter(({ selectedAsset }) => Boolean(selectedAsset?.latitude && selectedAsset?.longitude)),
      mergeMap(({ selectedAsset }) => {
        return of(
          AssetsActions.loadReverseGeocoding({ latitude: selectedAsset.latitude, longitude: selectedAsset.longitude })
        );
      })
    )
  );

  loadGeocodeAddressOnUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssetsActions.loadAssetsForIntervalSuccess),
        concatLatestFrom(() => this.store.select(selectChosenAsset)),
        map(([_, selectedAsset]) => {
          if (selectedAsset?.latitude && selectedAsset?.longitude) {
            this.store.dispatch(
              AssetsActions.loadReverseGeocoding({
                latitude: selectedAsset.latitude,
                longitude: selectedAsset.longitude
              })
            );
          }
        })
      ),
    { dispatch: false }
  );

  loadReverseGeocoding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.loadReverseGeocoding),
      mergeMap(({ latitude, longitude }) =>
        this.geocoder.getReverseGeocode(latitude, longitude).pipe(
          map(res => {
            const data = res.data[0];
            return AssetsActions.loadReverseGeocodingSuccess({ data });
          }),
          catchError((error: Error) => {
            this.datadog.addRumError(error);
            return of(AssetsActions.loadReverseGeocodingError({ error }));
          })
        )
      )
    )
  );

  loadNearbyAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.loadNearbyAssets),
      concatLatestFrom(() => [
        this.store.select(selectAssetParams),
        this.store.select(selectDefaultFilter),
        this.store.select(selectAllVehicles),
        this.store.select(selectAllSettings)
      ]),
      mergeMap(([{ selectedAsset }, assetsParams, defaultFilter, vehiclesMap, settings]) => {
        let params;
        if (assetsParams) {
          params = assetsParams;
        } else {
          // we go here in case a user has directly gone to /assets/:assetId, in this case,they would not yet have assetsParams in state, which prevented the nearby assets list from loading
          params = {
            companyId: defaultFilter.company?.id
          };
        }

        const nearbyAssetsParams: AssetsParams = {
          ...params,
          active: true,
          circle: [selectedAsset.latitude, selectedAsset.longitude, environment.nearbyAssets.searchRadiusMeters]
        };
        delete nearbyAssetsParams.searchTerms;
        delete nearbyAssetsParams.powerOn;
        return this.liveAssetsQuery.executeAssetQueryWithParams(nearbyAssetsParams).pipe(
          map(response => {
            response = response.filter(asset => asset.assetId !== selectedAsset.assetId);
            const assets = this.addMarkerDataToNearbyAssets(response, selectedAsset);
            const nearbyAssets = assets.filter(asset => {
              const searchRadius =
                asset.distanceUnit === PathDistanceUnits.KM
                  ? environment.nearbyAssets.searchRadiusInKm
                  : environment.nearbyAssets.searchRadiusInMiles;
              return asset.distanceFromSelectedAsset <= searchRadius;
            });
            return AssetsActions.loadNearbyAssetsSuccess({
              response: patchAssetsData(nearbyAssets, vehiclesMap, settings.assetViewType)
            });
          }),
          catchError((nearbyAssetsLoadError: Error) => {
            this.datadog.addRumError(nearbyAssetsLoadError);
            return of(AssetsActions.loadNearbyAssetsFailure({ nearbyAssetsLoadError }));
          }),
          takeUntil(this.httpCancelService.onCancelLocApiPendingRequests())
        );
      })
    )
  );

  addMarkerDataToNearbyAssets(nearbyAssets: ViewableAsset[], selectedAsset: ViewableAsset): ViewableAsset[] {
    const fromLatLng = [selectedAsset?.latitude, selectedAsset?.longitude];
    return nearbyAssets
      ?.map(asset => {
        const distance = this.uiUtils.convertDistance(getDistance(fromLatLng, [asset.latitude, asset.longitude]));
        return {
          ...asset,
          distanceFromSelectedAsset: distance,
          iconUrl: this.markerIconService.fetchAssetsListIconUrl(asset),
          className: this.markerIconService.fetchAssetClassName(asset),
          subTitle: this.uiUtils.assetSubtitle(asset),
          sidebarMessage: this.uiUtils.assetSidebarMessage(distance),
          markerSubtitle: this.uiUtils.assetSidebarMessage(distance),
          distanceUnit: this.uiUtils.getDistanceUnit()
        };
      })
      .sort((a, b) => a.distanceFromSelectedAsset - b.distanceFromSelectedAsset)
      .slice(0, environment.nearbyAssets.assetsToReturn);
  }

  addTripDataToPathFeatures(response: Trips, deviceType: string) {
    let inSegment = false;
    const featureCount = response.pointData.features.length;
    let startOdo;
    let startTime;
    let lastTripEndTime;
    let segIdx = 0;
    const isZTrak = deviceType === DeviceType.ZTRAK;
    response.pointData.features.forEach((f, i: number) => {
      f.tripStart = false;
      f.tripEnd = false;
      f.falsePowerEvent = false;
      f.isZTrak = isZTrak;
      if (!inSegment && f.properties.powerOn && !f.falsePowerEvent) {
        inSegment = true;
        f.tripStart = true;
        startOdo = f.properties.odometer;
        startTime = moment(f.properties.timestamp);
        if (segIdx > 0) {
          f.timeSincePreviousTrip = moment(f.properties.timestamp).diff(moment(lastTripEndTime));
        }
      }
      if (inSegment && (!f.properties.powerOn || i === featureCount - 1)) {
        if (
          i < featureCount - 1 &&
          moment(response.pointData.features[i + 1].properties.timestamp).diff(moment(f.properties.timestamp)) <
            environment.minimumTripMS &&
          i > 1 &&
          response.pointData.features[i - 1].properties.powerOn
        ) {
          f.falsePowerEvent = true;
          response.pointData.features[i + 1].falsePowerEvent = true;
        } else {
          f.tripEnd = true;
          lastTripEndTime = f.properties.timestamp;
          inSegment = false;
          f.tripDistance = f.properties.odometer - startOdo;
          f.tripDuration = moment(f.properties.timestamp).diff(startTime);
          segIdx++;
        }
      }
    });
    response.summary.powerOffCount = segIdx - 1;
    return response;
  }

  enrichPathFeatureProperties(
    response: Trips,
    associations: DriverVehicleAssociation[],
    driverActivities: Map<string, DriverActivity[]>
  ) {
    let distanceTravelled = 0;
    let accumulatedDistance = 0;
    return {
      ...response,
      pointData: {
        ...response.pointData,
        features: response.pointData.features.map((feature, index, features) => {
          if (index > 0) {
            const currentOdometer = feature?.properties?.odometer || 0;
            const prevOdometer = features[index - 1]?.properties?.odometer || 0;
            const diff = currentOdometer - prevOdometer;
            distanceTravelled += this.uiUtils.formatDistanceWithoutSuffix(diff);
            accumulatedDistance = round(distanceTravelled, 1);
          }
          return {
            ...feature,
            properties: {
              ...feature.properties,
              inputsFormatted: this.assetIoService.formatPathIos(feature.properties.inputs),
              accumulatedDistance,
              driverState:
                this.findDriverActivityForTimestamp(driverActivities, associations, feature.properties.timestamp)
                  ?.driverActivity || 'unknown'
            }
          };
        })
      }
    };
  }

  findDriverActivityForTimestamp(
    driverActivities: Map<string, DriverActivity[]>,
    associations: DriverVehicleAssociation[],
    timestamp: string
  ): DriverActivity {
    const target = DateTime.fromISO(timestamp, { zone: 'utc' });
    // find the driver card number associated with the asset at the given timestamp
    // then find the activity for that driver card number at the given timestamp
    if (associations?.length <= 0 || driverActivities.size == 0) {
      return null;
    }
    const foundAssociation = associations.find(association => {
      return this.isWithinRange(target, association.startAssociation, association.endAssociation);
    });
    if (!foundAssociation) {
      return null;
    }
    return (
      driverActivities.get(foundAssociation.driverCardNumber)?.find(activity => {
        return this.isWithinRange(target, activity.startActivity, activity.endActivity);
      }) || null
    );
  }

  isWithinRange(target: DateTime, start: DateTime, end?: DateTime | null): boolean {
    if (end) {
      return target >= start && target <= end;
    } else {
      return target >= start;
    }
  }

  enrichPathSummary(response) {
    const features = response.pointData.features;

    if (!features.length) {
      return response;
    }

    const ioCounts = [];

    features.forEach(feature => {
      const formattedInputs = this.assetIoService.formatPathIos(feature.properties.inputs);
      formattedInputs.forEach((io, i) => {
        if (io.change) {
          ioCounts[i] = ioCounts[i] || { label: io.label, count: 0 };
          ioCounts[i].count += 1;
        }
      });
    });

    return {
      ...response,
      summary: { ...response.summary, ioCounts }
    };
  }

  loadRecentPath$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssetsActions.loadRecentPath),
      switchMap(({ selectedAsset }) => {
        const startTimeBacktoZero = moment(selectedAsset.startTime).startOf('day').toDate();
        const endTime = moment(startTimeBacktoZero).endOf('day').toDate();
        // get all the driver_vehicle_associations
        return forkJoin([
          this.recentPathService.fetchRecentPath(selectedAsset, startTimeBacktoZero, endTime),
          this.driverAssetService.getDriverDetails(
            selectedAsset.companyId,
            selectedAsset.assetId,
            startTimeBacktoZero.toISOString(),
            endTime.toISOString()
          )
        ]).pipe(
          switchMap(([response, driverVehicleAssociation]) => {
            const associations: DriverVehicleAssociation[] =
              driverVehicleAssociation?.associations.map((association: ApiDriverAsset) => {
                return {
                  ...association,
                  startAssociation: DateTime.fromISO(association.startAssociation, { zone: 'utc' }),
                  endAssociation: DateTime.fromISO(association.endAssociation, { zone: 'utc' })
                };
              }) || [];
            // get activities for each driver card number based on association start and end time
            const driverActivityRequests: {
              driverCardNumber: string;
              request: Observable<DriverActivityResponse[]>;
            }[] = associations
              .filter(association => Boolean(association.driverCardNumber?.trim()))
              .map(association => ({
                driverCardNumber: association.driverCardNumber,
                request: this.driverStatusService.getDriverActivity(
                  association.driverCardNumber,
                  selectedAsset.companyId,
                  association.startAssociation.toJSDate(),
                  association.endAssociation.toJSDate()
                )
              }));
            if (driverActivityRequests?.length) {
              return forkJoin(driverActivityRequests.map(({ request }) => request)).pipe(
                map(driverActivityResponses => {
                  // combine driver card number with activities
                  // to make it easier to find activity by driver card number later
                  // in the enrichPathFeatureProperties method
                  const driverActivities: Map<string, DriverActivity[]> = new Map<string, DriverActivity[]>();
                  driverActivityResponses.forEach((activities, index) => {
                    const driverCardNumber: string = driverActivityRequests[index].driverCardNumber;
                    const previousActivities = driverActivities.has(driverCardNumber)
                      ? driverActivities.get(driverCardNumber)
                      : [];
                    const newActivities = activities.map(activity => ({
                      ...activity,
                      startActivity: DateTime.fromISO(activity.startActivity, { zone: 'utc' }),
                      endActivity: activity.endActivity ? DateTime.fromISO(activity.endActivity, { zone: 'utc' }) : null
                    }));
                    driverActivities.set(driverCardNumber, [...previousActivities, ...newActivities]);
                  });
                  return { response, associations, driverActivities };
                })
              );
            }
            return of({ response, associations, driverActivities: new Map<string, DriverActivity[]>() });
          }),

          map(({ response, associations, driverActivities }) => {
            this.leaflet.clearRecentPathLayer();
            this.closeOpenActivities(driverActivities);
            if (response.pointData?.features?.length > 0) {
              response.pointData.features[0].isFirst = true;
              response.pointData.features[response.pointData.features.length - 1].isLast = true;
              return {
                response: this.addTripDataToPathFeatures(
                  response,
                  this.uiUtils.gpssnToGpsType(selectedAsset.legacyAttributes.gpssn)
                ),
                associations,
                driverActivities
              };
            }
            return { response, associations, driverActivities };
          }),
          map(({ response, associations, driverActivities }) => {
            return this.enrichPathFeatureProperties(response, associations, driverActivities);
          }),
          map(response => {
            return this.enrichPathSummary(response);
          }),
          map(response => {
            return AssetsActions.loadRecentPathSuccess({ response: response });
          }),

          catchError((recentPathLoadError: Error) => {
            this.datadog.addRumError(recentPathLoadError);
            return of(AssetsActions.loadRecentPathFailure({ recentPathLoadError }));
          }),
          takeUntil(this.httpCancelService.onCancelPathApiPendingRequests())
        );
      })
    )
  );

  private closeOpenActivities(driverActivities: Map<string, DriverActivity[]>) {
    driverActivities.forEach(activities => {
      activities.sort((a, b) => a.startActivity.toMillis() - b.startActivity.toMillis());
      for (let i = 0; i < activities.length; i++) {
        if (activities[i].endActivity == null && i + 1 < activities.length) {
          activities[i].endActivity = activities[i + 1].startActivity.minus({ second: 1 });
        }
      }
    });
  }

  loadRecentPathSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssetsActions.loadRecentPathSuccess),
        map(action => {
          const recentPath = action.response.pointData;
          this.leaflet.showRecentPathLayer(recentPath);
        })
      ),
    { dispatch: false }
  );

  clearRecentPath$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssetsActions.clearRecentPath),
        map(() => {
          this.leaflet.clearRecentPathLayer();
        })
      ),
    { dispatch: false }
  );

  loadSelectedAssetById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssetsActions.loadSelectedAssetById),
      concatLatestFrom(() => [this.store.select(selectAllVehicles), this.store.select(selectAllSettings)]),
      switchMap(([action, vehiclesMap, settings]) => {
        return this.selectedAssetService.getSelectedAssetById(action.assetId).pipe(
          first(),
          map((asset: Asset) => {
            return AssetsActions.loadSelectedAsset({
              selectedAsset: patchAssetData(asset, vehiclesMap, settings.assetViewType)
            });
          }),
          catchError(assetLoadFailure => {
            this.datadog.addRumError(new Error(assetLoadFailure));
            return of(AssetsActions.loadSelectedAssetFailure({ failure: assetLoadFailure }));
          })
        );
      })
    );
  });

  resetSubscriptions() {
    this.assetsParams = null;
    this.cancelSubject$.next(true);
    this.httpCancelService.cancelLocApiPendingRequests();
  }

  // vehicles from vdo
  loadVehicles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssetsActions.loadVehicles),
      distinctUntilChanged((prev, next) => objectShallowEquality(prev.accountId, next.accountId)),
      filter(({ accountId }) => Boolean(accountId)),
      switchMap(({ accountId }) => {
        return this.vehiclesApi.getList(accountId).pipe(
          expand((response: VehicleList) => {
            const nextPage = response.page + 1;
            return nextPage < response.totalNumberOfPages ? this.vehiclesApi.getList(accountId, nextPage) : EMPTY;
          }),
          map(response => response.data ?? []),
          reduce<VehicleListItem[], VehicleListItem[]>(
            (allVehicles, vehicles) => [...allVehicles, ...vehicles],
            [] as VehicleListItem[]
          ),
          map(vehicles => {
            return AssetsActions.loadVehiclesSuccess({ response: vehicles });
          }),
          catchError((assetsLoadError: Error) => {
            this.datadog.addRumError(assetsLoadError);
            // can use same error action as assets
            return of(AssetsActions.loadAssetsFailure({ assetsLoadError }));
          })
        );
      })
    );
  });

  loadDaysWithActivity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssetsActions.loadDaysWithActivity),
      switchMap(({ accountId, assetId, startTime, endTime }) => {
        return this.fetchActivity(startTime, endTime, accountId, assetId).pipe(
          map(response => {
            const eventsWithActivityDates = response.daysWithActivities
              .filter(event => event?.hasActivity)
              .map(event => {
                const ts = parseInt(event.eventTimeStamp, 10);
                return DateTime.fromSeconds(ts).toFormat('dd-LL-yyyy');
              });
            const daysWithActivity = new Set<string>(eventsWithActivityDates);
            return AssetsActions.loadDaysWithActivitySuccess({
              response: { data: [...daysWithActivity] }
            });
          }),
          catchError((error: Error) => {
            this.datadog.addRumError(error);
            return of(AssetsActions.loadDaysWithActivityFailure({ error }));
          })
        );
      })
    );
  });

  /**
   * Fetch activity, splits the query to multiple queries with 3 day interval
   * @param endTime
   * @param startTime
   * @param accountId
   * @param assetId
   * @private
   */
  private fetchActivity(startTime: string, endTime: string, accountId: string, assetId: string) {
    const intervals = Interval.fromISO(`${startTime}/${endTime}`).splitBy({ day: 3 });
    return from(intervals).pipe(
      mergeMap(
        (interval: Interval) =>
          this.recentPathService.fetchDaysWithActivity(
            accountId,
            assetId,
            interval.start.toUTC().toISO(),
            interval.end.toUTC().toISO()
          ),
        3
      ),
      reduce(
        (combinedResponse: DaysWithActivityResponse, response) => {
          combinedResponse.daysWithActivities.push(...response.daysWithActivities);
          return combinedResponse;
        },
        { daysWithActivities: [] }
      )
    );
  }
}
